/**
 * PublicAnalyticsBeacon — fire behaviour events from public proposal pages.
 *
 * Phase 2 of the proposal analytics enhancement: this component is mounted on
 * `/p/:token`, `/p/:token/narrative` and `/p/:token/terms`. It:
 *   - sends a 15s "heartbeat" event while the page is in the foreground
 *   - reports section visibility via IntersectionObserver on `data-pv-section`
 *   - reports the max scroll-depth reached
 *   - flushes a final `pagehide` with cumulative dwell on tab close
 *
 * All requests are best-effort POSTs to /api/public/proposals/{token}/event
 * — failures are swallowed silently so the client UX is never affected.
 *
 * GDPR: no third-party scripts, no fingerprinting beyond UA already in the
 * request, and no DOM content is exfiltrated — only section IDs we own.
 */
import { useEffect, useRef } from 'react';
import { API_BASE } from '../api';

const HEARTBEAT_INTERVAL_MS = 15000;

export default function PublicAnalyticsBeacon({ token, viewKind }) {
    const startedAt = useRef(Date.now());
    const lastBeatAt = useRef(Date.now());
    const maxScroll = useRef(0);
    const sectionsSeen = useRef(new Set());

    useEffect(() => {
        if (!token) return undefined;

        const send = (payload, useBeacon = false) => {
            try {
                const url = `${API_BASE}/public/proposals/${encodeURIComponent(token)}/event`;
                const body = JSON.stringify({ view_kind: viewKind, ...payload });
                if (useBeacon && navigator.sendBeacon) {
                    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
                } else {
                    fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body,
                        keepalive: true,
                    }).catch(() => {});
                }
            } catch { /* swallow */ }
        };

        // ── Heartbeat — only when the tab is visible ──────────────────────
        const heartbeat = () => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            const elapsed = now - lastBeatAt.current;
            lastBeatAt.current = now;
            send({ kind: 'heartbeat', duration_ms: elapsed });
        };
        const interval = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);

        // ── Scroll depth ──────────────────────────────────────────────────
        const onScroll = () => {
            const docH = document.documentElement.scrollHeight || 1;
            const winH = window.innerHeight || 0;
            const scrolled = window.scrollY + winH;
            const pct = Math.min(100, Math.max(0, Math.round((scrolled / docH) * 100)));
            if (pct > maxScroll.current) {
                maxScroll.current = pct;
                // Throttle scroll reports to every +10% step
                if (pct % 10 === 0) {
                    send({ kind: 'scroll', scroll_pct: pct });
                }
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });

        // ── Section visibility via [data-pv-section="…"] elements ────────
        let observer;
        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        const sec = entry.target.getAttribute('data-pv-section');
                        if (sec && entry.isIntersecting && !sectionsSeen.current.has(sec)) {
                            sectionsSeen.current.add(sec);
                            send({ kind: 'section', section: sec });
                        }
                    });
                },
                { threshold: 0.5 }
            );
            // Discover sections — re-scan once after mount to catch late-injected nodes.
            const attach = () => {
                document.querySelectorAll('[data-pv-section]').forEach(el => observer.observe(el));
            };
            attach();
            const reattach = setTimeout(attach, 1500);
            // Page-as-a-section fallback so we always log at least one section visit
            send({ kind: 'section', section: viewKind || 'page' });
            return () => {
                clearTimeout(reattach);
                clearInterval(interval);
                window.removeEventListener('scroll', onScroll);
                observer.disconnect();
                send({
                    kind: 'pagehide',
                    scroll_pct: maxScroll.current,
                    duration_ms: Date.now() - startedAt.current,
                }, true);
            };
        }

        return () => {
            clearInterval(interval);
            window.removeEventListener('scroll', onScroll);
            send({
                kind: 'pagehide',
                scroll_pct: maxScroll.current,
                duration_ms: Date.now() - startedAt.current,
            }, true);
        };
    }, [token, viewKind]);

    // Also flush on browser tab close via pagehide event
    useEffect(() => {
        const onHide = () => {
            try {
                const url = `${API_BASE}/public/proposals/${encodeURIComponent(token)}/event`;
                const body = JSON.stringify({
                    kind: 'pagehide',
                    view_kind: viewKind,
                    scroll_pct: maxScroll.current,
                    duration_ms: Date.now() - startedAt.current,
                });
                if (navigator.sendBeacon) {
                    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
                }
            } catch { /* swallow */ }
        };
        window.addEventListener('pagehide', onHide);
        return () => window.removeEventListener('pagehide', onHide);
    }, [token, viewKind]);

    return null;
}
